import { useContext, useEffect, useState } from "react";
import { skipToken } from "@reduxjs/toolkit/query";
import isEqual from "lodash/isEqual";
import pick from "lodash/pick";
import sortBy from "lodash/sortBy";

import {
  useGetBroadbandProvidersQuery,
  useGetMultiProjectQuery,
  useGetMultiProjectStatisticsQuery,
  useGetVersionConfigQuerySubscription,
  versionsSlice,
} from "fond/api";
import { defaultCityPlannerLayerFilters, generateMultiProjectMapConfiguration, OverlayIds, SUBAREAS_KEY } from "fond/cityPlanner/configuration";
import { LayerFilterContext } from "fond/cityPlanner/LayerFilterContext";
import { markExcludedConfigs } from "fond/hooks/useFilterLayers";
import { MapContext } from "fond/map/MapProvider";
import { useMapBounds } from "fond/map/useMapBounds";
import { MapboxStyleLayer, Maybe, MultiProjectArea } from "fond/types";
import { useAppDispatch } from "fond/utils/hooks";

export const getFilteredProviders = (filters: Record<string, string[]>): Set<string> => {
  // The big boys are always included.
  const providers = new Set<string>(["AT&T", "Verizon", "T-Mobile"]);

  // The current list of broadband providers only includes those providers that have coverage in the current
  // map bounds. We also want to include any providers that were already selected but may not have coverage.
  if (OverlayIds.FCC in filters) {
    filters[OverlayIds.FCC].forEach((layerId) => {
      if (layerId.endsWith("-service-types") && layerId.startsWith("fcc-")) {
        // fcc-<Provider>-service-types => <Provider>
        const providerName = layerId.substring(4, layerId.length - 14);
        providers.add(providerName);
      }
    });
  }

  return providers;
};

type BaseArea = Pick<MultiProjectArea, "ID" | "Name">;

const getAreas = (areas: Maybe<MultiProjectArea[]>): BaseArea[] => {
  const result = (areas ?? []).map((a) => pick(a, ["ID", "Name"]));
  return sortBy(result, ["ID", "Name"]);
};

export const FIBER_PROVIDERS_MIN_ZOOM = 7;

// Dynamically inject layer configurations for each selected broadband provider.
export const useMultiProjectConfig = (multiProjectId: Maybe<string>): void => {
  const dispatch = useAppDispatch();
  const context = useContext(LayerFilterContext);
  const { map } = useContext(MapContext) ?? {};
  const { data: statistics } = useGetMultiProjectStatisticsQuery(multiProjectId ?? skipToken);
  const { data: multiProject } = useGetMultiProjectQuery(multiProjectId ?? skipToken);
  const [areas, setAreas] = useState<BaseArea[]>([]);

  // Keep track of the areas in state to avoid re-calculating configuration every time the multiProject changes
  // in some unrelated way.
  useEffect(() => {
    const newAreas = getAreas(multiProject?.Areas);
    setAreas((previous) => {
      if (isEqual(previous, newAreas)) {
        return previous; // stable ref
      } else {
        return newAreas;
      }
    });
  }, [multiProject?.Areas, setAreas]);

  // Fetch fiber providers for the combined fiber provider coverage layer.
  // Skip the query when zoomed out past the threshold to avoid fetching an excessive number of providers.
  const { bounds: mapBounds, zoom } = useMapBounds({ skip: !multiProjectId });
  const accountId = multiProject?.Account?.ID;
  const isZoomedInEnough = zoom !== null && zoom >= FIBER_PROVIDERS_MIN_ZOOM;
  const { data: fiberProvidersData } = useGetBroadbandProvidersQuery(
    accountId && mapBounds && multiProjectId && isZoomedInEnough ? { accountId, bounds: mapBounds, serviceType: "fiber" } : skipToken
  );

  // Manually create a subscription to the getVersionConfig endpoint to avoid RTK clearing cache.
  // This is required as we can not actually hit the endpoint as the BE does not have a config for market lens projects.
  useGetVersionConfigQuerySubscription(multiProjectId ?? skipToken);

  // Remove subarea layers when areas or statistics change so they can be recreated with updated addressMatch expressions.
  // This ensures address counts appear immediately after area import without requiring a page refresh.
  useEffect(() => {
    if (!map || !multiProject?.ID) return;

    const layerIds = (map.getStyle()?.layers ?? [])
      .filter((layer) => (layer as MapboxStyleLayer).metadata?.layerKey === SUBAREAS_KEY)
      .map((layer) => layer.id);

    layerIds.forEach((id) => {
      if (map.getLayer(id)) {
        map.removeLayer(id);
      }
    });

    // We don't add map to the dependency array because we don't want to remove layers when the map changes, only when areas or statistics change.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [areas, multiProject?.ID, statistics?.Areas]);

  // Update the configuration whenever anything affecting the configuration changes.
  useEffect(() => {
    if (!multiProject?.ID) return;

    const providers = context?.filters ? Array.from(getFilteredProviders(context.filters)) : [];

    const data = generateMultiProjectMapConfiguration(
      { ID: multiProject.ID, SystemOfMeasurement: multiProject.SystemOfMeasurement, Areas: areas },
      statistics?.Areas ?? [],
      providers,
      isZoomedInEnough ? (fiberProvidersData ?? []) : []
    );

    dispatch(
      versionsSlice.util.upsertQueryData(
        "getVersionConfig",
        multiProject.ID,
        markExcludedConfigs(data, { ...defaultCityPlannerLayerFilters, ...context?.filters })
      )
    );
  }, [
    context?.filters,
    multiProject?.ID,
    multiProject?.SystemOfMeasurement,
    areas,
    statistics?.Areas,
    fiberProvidersData,
    dispatch,
    isZoomedInEnough,
  ]);
};
